import { BlurView } from 'expo-blur';
import { useEffect, useState } from 'react';
import { AccessibilityInfo, Platform, StyleSheet, useColorScheme, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { View } from 'tamagui';

import { useGlassTint } from '@/components/glass-surface';
import { dockTranslateY } from '@/lib/dock';
import { useIsDesktop } from '@/lib/use-desktop';
import { shadowStyle } from '@/theme/shadow';
import {
  borderWidth,
  darkColors,
  fontSize,
  lightColors,
  lineHeight,
  radius,
  shadow,
  space,
} from '@/theme/tokens.generated';

// Same glass material every other surface in the app uses as of
// 2026-09-16 (founder: "layers everywhere like even the bottom bar").
// Deliberately NOT combined with `tabBarStyle: { position: 'absolute' }`
// (the usual "content scrolls under a floating glass bar" trick) --
// that would pull the tab bar out of layout flow, and every screen in
// this Tabs navigator would then need its OWN bottom padding to match
// the bar's real height or its last row of content would render
// underneath it. That's a real, multi-screen layout risk this pass
// doesn't need to take on for a bottom-bar polish pass -- the bar
// keeps its normal (in-flow) position, so nothing else changes, and
// still gets the same frosted material and warm tint as every other
// surface in this pass.
// Floating "liquid glass" dock (borrowed from the aaeti app's tab bar): a pill
// that blurs and tints what is behind it, edged with a bright top highlight like
// light catching glass, lifted off the page by a soft shadow. The bar stays IN
// layout flow (see the note above), so it floats over the page background rather
// than over scrolling content; scroll-under is a separate, per-screen change.
const DOCK_MAX_WIDTH = 448;
const DOCK_ITEM_HEIGHT = 64;
const DOCK_BOTTOM_GAP = 8;

// Total height of the dock, safe-area padding included: what the layout provides
// to every screen (lib/dock.ts DockInsetContext).
export function useDockHeight(): number {
  const isDesktop = useIsDesktop();
  const { paddingBottom } = useDock();
  // On desktop the dock is a left rail, so it covers no content at all.
  return isDesktop ? 0 : DOCK_ITEM_HEIGHT + paddingBottom;
}

function useDock() {
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();

  return {
    // Narrow phones keep a 16px gutter; wide screens centre a 448px capsule.
    gutter: Math.max(16, Math.round((width - DOCK_MAX_WIDTH) / 2)),
    paddingBottom: (Platform.OS !== 'web' ? insets.bottom : Math.max(insets.bottom, 4)) + DOCK_BOTTOM_GAP,
  };
}

// Reduced transparency (an OS accessibility setting) or no backdrop-filter at all:
// the dock must stay legible, so it paints an opaque surface instead of glass.
function useSolidDock(): boolean {
  const [solid, setSolid] = useState(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return false;
    const supported =
      typeof CSS !== 'undefined' &&
      (CSS.supports('backdrop-filter', 'blur(1px)') || CSS.supports('-webkit-backdrop-filter', 'blur(1px)'));
    const reduced =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-transparency: reduce)').matches;
    return !supported || reduced;
  });

  useEffect(() => {
    if (Platform.OS === 'web') {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
      const query = window.matchMedia('(prefers-reduced-transparency: reduce)');
      const onChange = () => setSolid(query.matches);
      query.addEventListener('change', onChange);
      return () => query.removeEventListener('change', onChange);
    }

    AccessibilityInfo.isReduceTransparencyEnabled?.().then(setSolid, () => {});
    const sub = AccessibilityInfo.addEventListener('reduceTransparencyChanged', setSolid);
    return () => sub.remove();
  }, []);

  return solid;
}

// The active tab's indicator: a soft pill behind the icon (Material 3 / iOS
// style), drawn here so it is always a clean stadium. Weight and colour change
// too, so the active state is never colour alone.
export function DockIcon({ focused, children }: { focused: boolean; children: React.ReactNode }) {
  const colorScheme = useColorScheme();
  const accent = colorScheme === 'dark' ? darkColors.textLink : lightColors.textLink;

  return (
    <View
      style={{
        width: 56,
        height: 30,
        borderRadius: radius.full,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: focused ? `${accent}24` : 'transparent',
      }}
    >
      {children}
    </View>
  );
}

function TabBarBackground() {
  const tint = useGlassTint();
  const solid = useSolidDock();
  const colors = tint.isDark ? darkColors : lightColors;
  const { gutter, paddingBottom } = useDock();
  const webGlass =
    Platform.OS === 'web'
      ? ({
          // Saturation lifts the colours passing behind the glass, the part a
          // plain blur lacks; the blur itself stays small so shapes still read.
          backdropFilter: solid ? 'none' : 'blur(6px) saturate(1.8)',
          WebkitBackdropFilter: solid ? 'none' : 'blur(6px) saturate(1.8)',
          boxShadow: tint.isDark
            ? `${shadow.dark[2].boxShadow}, inset 0 1px 0 rgba(255,255,255,0.16), inset 0 -1px 0 rgba(0,0,0,0.3)`
            : `${shadow.light[2].boxShadow}, inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 0 rgba(0,0,0,0.08)`,
        } as object)
      : null;

  return (
    <View style={StyleSheet.absoluteFill} pointerEvents="none">
      <View
        style={[
          {
            position: 'absolute',
            left: gutter,
            right: gutter,
            top: 0,
            bottom: paddingBottom,
            borderRadius: radius.full,
            overflow: 'hidden',
            borderWidth: borderWidth.control,
            borderColor: tint.border,
            backgroundColor: solid ? colors.surfaceCard : tint.overlay,
            // iOS/Android shadow-2 (web uses boxShadow below).
            ...shadowStyle(2, tint.isDark, false),
          },
          webGlass,
        ]}
      >
        {Platform.OS !== 'web' && !solid && (
          <BlurView
            intensity={tint.isDark ? 32 : 46}
            tint={tint.isDark ? 'dark' : 'light'}
            style={StyleSheet.absoluteFill}
          />
        )}
      </View>
    </View>
  );
}

// The tab bar's chrome: tints, label/item/tabBar styles, glass
// background, and the web tab-bar height formula. Extracted from
// app/(app)/_layout.tsx (2026-09) so that layout file is composition
// only -- its history was two unrelated change-magnets fused into one
// file (every new screen registration AND every safe-area incident,
// #269/#297 among them, landed in the same diff). Everything in here
// moved verbatim, numeric values and comments included -- do not
// "simplify" the formula; it encodes real incidents.
export function useTabBarScreenOptions() {
  // The capsule's gutter and bottom padding (also used by TabBarBackground).
  const dock = useDock();
  // React Navigation's tab bar doesn't read Tamagui's theme (same gap
  // root _layout.tsx's navigationTheme fixes for Stack/header chrome --
  // see that file's doc comment) -- tabBarActiveTintColor/
  // tabBarInactiveTintColor are set directly off the same generated
  // token colors so the tab bar's icon/label tint actually swaps with
  // dark mode instead of falling back to React Navigation's own
  // default blue.
  const colorScheme = useColorScheme();
  const colors = colorScheme === 'dark' ? darkColors : lightColors;

  // Issue #269/#272, #297: real device insets flow through on web via
  // public/index.html's `viewport-fit=cover` meta. On this founder's
  // actual devices env(safe-area-inset-bottom) reads 0 regardless of
  // browser (confirmed via #303's on-device debug overlay across
  // Safari, Brave, Chrome, and Android), so `insets.bottom` alone can't
  // be trusted to carry real bottom clearance -- this floor is it.
  //
  // The real, separate bug that made every padding-value tweak here
  // INVISIBLE for a full day (root-caused by reading the actual
  // bundled library source, node_modules/expo-router/build/
  // react-navigation/bottom-tabs/views/BottomTabBar.js's own
  // getTabBarHeight): unless tabBarStyle sets an explicit numeric
  // `height`, the bar's TOTAL height is hardcoded to
  // `TABBAR_HEIGHT_UIKIT (49) + insets.bottom`, using React
  // Navigation's OWN internal insets -- a completely separate read from
  // this value. With that insets.bottom reading 0, the container's
  // total height was fixed at 49px no matter what this padding
  // computed to; the padding was applied correctly but had nowhere to
  // grow into, so it just ate space out of the icon/label row instead
  // of pushing the bar's actual bottom edge down. `webTabBarHeight`
  // below fixes that by mirroring the library's own formula with this
  // value substituted in -- verify against that file's actual current
  // source before touching either number again.
  //
  // The padding value itself, 4: the founder's own read after the
  // height fix started genuinely working (confirmed both by direct DOM
  // measurement and by them finally seeing a real visual change) --
  // 34px read as "too padded," and their fix, stated directly, was to
  // match `tabBarItemStyle`'s own `paddingTop: 4` below exactly, not a
  // separate safe-area buffer with its own reasoning. Taken literally.
  const webTabBarPaddingBottom = dock.paddingBottom;
  // 49 == TABBAR_HEIGHT_UIKIT, read directly from BottomTabBar.js above
  // -- the same base content height React Navigation itself assumes.
  // Web-only: native's own insets.bottom already matches what the
  // library's internal height calc uses, so native has no divergence
  // to correct here.

  const isDesktop = useIsDesktop();

  const base = {
    // textLink, not actionPrimary: the raw brand red is only 2.1-2.9:1 on the dark glass.
    tabBarActiveTintColor: colors.textLink,
    tabBarInactiveTintColor: colors.textTertiary,
    // Issue #147 AC2 (absorbs #86's remaining scope): icons already
    // existed (ThreadIcon/MatchesIcon/ProfileIcon), but the LABEL
    // below each one still rendered in React Navigation's own
    // default system-font/size -- the "stock RN chrome" look this
    // AC calls out, plainly different from every other piece of
    // text in this app (Libre Franklin everywhere else, never the
    // platform system font). `tabBarLabelStyle` is a raw RN style
    // object (not a Tamagui-token-aware prop), so the literal
    // values below are the ACTUAL loaded font family name (see
    // tamagui.config.shared.ts's bodyFont face map) and the small,
    // "tiny caption" size #86's own AC asks for -- matching
    // Instagram/TikTok's icon+tiny-label convention rather than
    // dropping the label entirely, since a visible label (even
    // small) is more discoverable than icon-only for a young
    // product's users who haven't yet memorized the iconography.
    // flexShrink: 0 -- founder-reported live, 2026-09-15: "the bar at
    // the bottom is cut off (can't see full text)". Root-caused via a
    // live CDP CSS.getMatchedStylesForNode query against the deployed
    // app: no rule anywhere sets an explicit height on the label: its
    // computed height (6px, for an 11px font) is a pure flexbox
    // compression artifact. React Navigation's own Label wraps RN Text
    // with numberOfLines={1}, which on react-native-web applies
    // `overflow: hidden` (its textOneLine style) -- per the CSS
    // Flexbox spec, a flex item with non-visible overflow gets an
    // automatic minimum size of 0 instead of its content size, so the
    // label is free to be squeezed below its natural text height by
    // its flex-column sibling (the icon) whenever the tab item's total
    // height is tight. flexShrink: 0 is the standard fix: it stops the
    // label being compressed at all, regardless of the icon/padding
    // math elsewhere in this file.
    tabBarLabelStyle: {
      fontFamily: 'LibreFranklin_500Medium',
      fontSize: fontSize.caption,
      lineHeight: lineHeight.caption,
      // `as const` only pins the literal for RN's TextStyle union (the
      // style object now comes from a hook's return instead of an
      // inline literal with contextual typing) -- the value is
      // unchanged from _layout.tsx.
      fontWeight: '500' as const,
      flexShrink: 0,
    },
    // justifyContent: 'center' -- issue #297, root-caused from a real
    // iOS Safari screenshot (mobile web, address bar visible), not
    // from headless-Chrome measurement (which cannot reproduce
    // Safari's dynamic-toolbar safe-area behavior and consistently
    // reported "correct" numbers here across three prior fix
    // attempts, PRs #281/#282/#283). Founder's own description: "not
    // even a pixel between the text... and the edge of the screen...
    // the bottom bar content is not centred... space on top of the
    // bottom bar vs the bottom." That's a DIFFERENT bug class than
    // every prior attempt assumed -- those all iterated on the
    // padding VALUE (Math.max(insets.bottom, N)), on the theory that
    // some number was wrong. The actual symptom is content flush to
    // one edge of its own row with a gap on the OTHER side -- i.e.
    // not centered within whatever height the row ends up being, on
    // this specific device/browser-chrome state -- independent of
    // what that height computes to. Forcing explicit centering here
    // fixes the symptom regardless of the exact insets/height math on
    // any given real device, rather than chasing another guess at
    // the right padding number.
    // The active mark is a soft pill behind the item (never colour alone).
    tabBarItemStyle: {
      paddingTop: space[1],
      justifyContent: 'center' as const,
    },
    // tabBarStyle's background/border USED to come entirely from
    // `navigationTheme` (root _layout.tsx's theme.colors.card/
    // border). backgroundColor: 'transparent' here is what actually
    // lets TabBarBackground's BlurView show through -- React
    // Navigation paints tabBarStyle's own background ON TOP of
    // whatever tabBarBackground renders, so leaving the opaque
    // theme color in place would have hidden the blur completely
    // (confirmed against @react-navigation/bottom-tabs' own
    // BottomTabBar, which layers tabBarBackground first, then this
    // style, then the bar's content). borderTopColor stays a real,
    // resolved value (not a Tamagui token -- this is a raw RN
    // style object, same reasoning as tabBarLabelStyle above it).
    tabBarStyle: {
      backgroundColor: 'transparent',
      borderTopColor: colors.borderDefault,
      // See webTabBarPaddingBottom's own 10th-recurrence comment
      // above -- height is real this time, derived from the exact
      // formula BottomTabBar.js's own getTabBarHeight uses
      // internally (TABBAR_HEIGHT_UIKIT + inset), just substituting
      // our own webTabBarPaddingBottom for its internal
      // insets.bottom. undefined on native, matching this style
      // object's existing web-only pattern -- React Navigation's
      // own auto-height is correct there (its internal insets.bottom
      // already matches ours exactly, no divergence to correct).
      // Absolute so content scrolls beneath the glass; every screen reserves or
      // pads by the same height (lib/dock.ts). The translate lets it slide away.
      position: 'absolute' as const,
      left: 0,
      right: 0,
      bottom: 0,
      transform: [{ translateY: dockTranslateY }],
      height: DOCK_ITEM_HEIGHT + webTabBarPaddingBottom,
      paddingBottom: webTabBarPaddingBottom,
      paddingHorizontal: dock.gutter,
      borderTopWidth: 0,
    },
    tabBarBackground: TabBarBackground,
  };

  // >=1024px: the floating pill becomes a quiet left rail in the page's own
  // surface, in flow, so nothing can ever scroll beneath it.
  if (!isDesktop) return base;

  return {
    ...base,
    tabBarPosition: 'left' as const,
    tabBarVariant: 'material' as const,
    tabBarStyle: {
      backgroundColor: colors.surfaceCard,
      borderRightColor: colors.borderDefault,
      borderRightWidth: borderWidth.hairline,
    },
    tabBarBackground: undefined,
  };
}
